import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators';

import { isDefined } from '../../helpers/utils';
import { loadGoogleMapsApi } from '../../helpers/api/google-api';

@customElement('geofence-map-picker')
export class GeofenceMapPickerComponent extends LitElement {
	@property({ type: Number })
	lat: number = 0;

	@property({ type: Number })
	lng: number = 0;

	@property({ type: Number })
	radiusMeters: number = 100;

	@query('#map-container')
	private readonly mapContainer!: HTMLDivElement;

	private map?: google.maps.Map;
	private circle?: google.maps.Circle;

	async firstUpdated() {
		await loadGoogleMapsApi();
		this.initMap();
	}

	updated(changedProperties: Map<string, any>) {
		// If parent updated lat/lng/radius (via inputs), update the map and circle
		if (changedProperties.has('lat') || changedProperties.has('lng') || changedProperties.has('radiusMeters')) {
			if (isDefined(this.map) && isDefined(this.circle)) {
				const newCenter = { lat: this.lat, lng: this.lng };
				this.circle.setCenter(newCenter);
				this.circle.setRadius(this.radiusMeters);
				this.map.panTo(newCenter);
			}
		}
	}

	private initMap() {
		if (!isDefined(this.mapContainer)) {
			console.error('Map container not found');
			return;
		}

		const center = { lat: this.lat, lng: this.lng };
		this.map = new google.maps.Map(this.mapContainer, {
			center,
			zoom: 13,
			disableDefaultUI: true,
			gestureHandling: 'cooperative',
			zoomControl: true,
			mapTypeId: google.maps.MapTypeId.ROADMAP,
		});

		// Add click listener to set center
		this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
			if (event.latLng) {
				const newLat = event.latLng.lat();
				const newLng = event.latLng.lng();
				this.dispatchGeofenceChanged(newLat, newLng, this.radiusMeters);
			}
		});

		this.initCircle();
	}

	private initCircle() {
		if (!isDefined(this.map)) {
			return;
		}

		const center = { lat: this.lat, lng: this.lng };
		this.circle = new google.maps.Circle({
			map: this.map,
			center,
			radius: this.radiusMeters,
			draggable: true,
			editable: true,
			strokeColor: '#007bff',
			strokeOpacity: 0.8,
			strokeWeight: 2,
			fillColor: '#007bff',
			fillOpacity: 0.15,
		});

		// Listen for center changes (dragging the circle)
		this.circle.addListener('center_changed', () => {
			const newCenter = this.circle?.getCenter();
			if (newCenter) {
				this.dispatchGeofenceChanged(newCenter.lat(), newCenter.lng(), this.radiusMeters);
			}
		});

		// Listen for radius changes (dragging the edge handle)
		this.circle.addListener('radius_changed', () => {
			const newCenter = this.circle?.getCenter();
			const newRadius = this.circle?.getRadius();
			if (newCenter && newRadius !== undefined) {
				this.dispatchGeofenceChanged(newCenter.lat(), newCenter.lng(), newRadius);
			}
		});
	}

	private dispatchGeofenceChanged(lat: number, lng: number, radiusMeters: number) {
		this.dispatchEvent(
			new CustomEvent('geofence-changed', {
				detail: { lat, lng, radius_meters: radiusMeters },
				bubbles: true,
				composed: true,
			})
		);
	}

	render() {
		return html`<div id="map-container"></div>`;
	}

	static styles = css`
		:host {
			display: block;
		}

		#map-container {
			width: 100%;
			height: 280px;
			border-radius: 6px;
			overflow: hidden;
			border: 1px solid #e0e0e0;
		}
	`;
}
